import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { HealthRing, Empty } from "../components/ui";
import { buildGraphFromWorkspace } from "../lib/graphFromWorkspace";
import { instanceById, contractsForOrg } from "../lib/graph";
import { LayoutGrid, type ProjectData } from "../components/widgets";
import { LayoutEditor } from "../components/LayoutEditor";
import * as work from "../lib/workClient";
import type { Layout } from "../lib/types";

export default function Project360() {
  const { id } = useParams<{ id: string }>();
  const { ws } = useWorkspace();
  const g = useMemo(() => buildGraphFromWorkspace(ws), [ws]);
  const instance = id ? instanceById(g, id) : undefined;
  const account = instance ? ws.accounts.find((a) => a.id === instance.accountId) : undefined;

  const [mode, setMode] = useState<work.Mode>("local");
  const [layout, setLayout] = useState<Layout | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => { work.loadLayouts(ws).then((r) => { setMode(r.mode); setLayout(r.layouts.find((l) => l.entity === "project") ?? null); }); }, [ws]);

  if (!instance || !account) {
    return <div className="page"><Empty><p style={{ marginBottom: 12 }}>That project could not be found.</p><Link to="/relationships" className="btn btn--ghost btn--sm"><Icon name="arrowRight" size={14} /> Back to relationships</Link></Empty></div>;
  }

  // resolve the underlying deployment record for infra details
  const depId = instance.id.replace(/^inst_/, "");
  const dep = (ws.records?.deployment ?? []).find((d) => d.id === depId);
  const project: ProjectData = {
    id: instance.id, name: instance.name, accountId: account.id, health: instance.health,
    region: dep ? String(dep.attributes.region ?? "") : account.region,
    seatsProvisioned: dep ? Number(dep.attributes.seats_provisioned) : account.seats,
    uptime: dep ? Number(dep.attributes.uptime_30d) : undefined,
    version: dep ? String(dep.attributes.version ?? "") : undefined,
  };
  const contract = contractsForOrg(g, account.id)[0];

  const save = async (sections: Layout["sections"]) => {
    if (!layout) return;
    setLayout({ ...layout, sections });
    setEditing(false);
    await work.saveLayout(mode, ws, layout.id, sections);
  };

  return (
    <div className="page">
      {editing && layout && <LayoutEditor layout={layout} entity="project" onSave={save} onClose={() => setEditing(false)} />}

      <p className="eyebrow" style={{ marginBottom: 14 }}>
        <Link to="/accounts" className="faint">Accounts</Link> · <Link to={`/accounts/${account.id}`} className="faint">{account.name}</Link> · Project
      </p>

      <div className="detail-hd">
        <span className="mono mono--lg"><Icon name="layers" size={22} /></span>
        <div className="meta">
          <h1>{instance.name}</h1>
          <div className="subline">
            <span>{project.region}</span>
            {contract && <span>{contract.name}</span>}
            <span>{project.seatsProvisioned} seats</span>
            {project.uptime != null && <span>{project.uptime}% uptime</span>}
          </div>
        </div>
        <div className="row" style={{ gap: 20, alignItems: "center" }}>
          <HealthRing value={instance.health} size={72} stroke={6} />
          <button className="btn btn--ghost btn--sm" onClick={() => setEditing(true)}><Icon name="grid" size={14} /> Customize</button>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        {layout ? <LayoutGrid sections={layout.sections} ctx={{ entity: "project", ws, account, project }} /> : <Empty>Loading layout…</Empty>}
      </div>
    </div>
  );
}
